import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { processAttachment } from "@/lib/image";
import { toast } from "sonner";
import { Camera, Trash2, Loader2, FileText, ImageIcon, Eye, ImagePlus } from "lucide-react";

const humanSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const Attachments = ({ jobId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(null); // { id, name, mime, data }
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  // Cache of fetched data URLs keyed by attachment id — populated once per
  // attachment and reused for both thumbnails and full-size preview to avoid
  // refetching (attachments can be up to 5 MB each).
  const cacheRef = useRef(new Map());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/jobs/${jobId}/attachments`);
      setItems(r.data);
    } catch {
      toast.error("Could not load attachments");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // Reset cache when switching jobs
    cacheRef.current = new Map();
    /* eslint-disable-next-line */
  }, [jobId]);

  const getData = useCallback(async (attId) => {
    const cache = cacheRef.current;
    if (cache.has(attId)) return cache.get(attId);
    const r = await api.get(`/jobs/${jobId}/attachments/${attId}/data`);
    const dataUrl = r.data.data;
    cache.set(attId, dataUrl);
    return dataUrl;
  }, [jobId]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0, fail = 0;
    try {
      for (const file of files) {
        try {
          const processed = await processAttachment(file);
          if (processed.size > 5 * 1024 * 1024) {
            toast.error(`${file.name}: too large after compression (5 MB max)`);
            fail += 1;
            continue;
          }
          await api.post(`/jobs/${jobId}/attachments`, processed);
          ok += 1;
        } catch (err) {
          fail += 1;
          toast.error(`${file.name}: ${err?.response?.data?.detail || err.message || "upload failed"}`);
        }
      }
      await load();
      if (ok > 0 && fail === 0) toast.success(ok === 1 ? "Attachment saved" : `${ok} attachments saved`);
      else if (ok > 0 && fail > 0) toast.warning(`${ok} saved, ${fail} failed`);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const openAttachment = async (att) => {
    try {
      const data = await getData(att.id);
      setPreviewing({ ...att, data });
    } catch {
      toast.error("Could not open attachment");
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete ${att.name}?`)) return;
    try {
      await api.delete(`/jobs/${jobId}/attachments/${att.id}`);
      cacheRef.current.delete(att.id);
      setItems((xs) => xs.filter((x) => x.id !== att.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="border-t-2 border-foreground p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Photos &amp; files</div>
        <div className="font-mono text-[10px] text-muted-foreground">{items.length}</div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="btn-ghost-rugged text-[11px] py-2 inline-flex items-center justify-center gap-2 cursor-pointer">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          <span className="hidden md:inline">Take photo</span>
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            data-testid="attach-camera"
            onChange={(e) => uploadFiles(e.target.files)} />
        </label>
        <label className="btn-ghost-rugged text-[11px] py-2 inline-flex items-center justify-center gap-2 cursor-pointer">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          <span className="hidden md:inline">From gallery</span>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden"
            data-testid="attach-upload"
            onChange={(e) => uploadFiles(e.target.files)} />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-1">
        {loading && <div className="col-span-2 font-mono text-[10px] uppercase text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="col-span-2 border-2 border-dashed border-foreground/40 p-4 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            No photos yet. Snap paint marks &amp; meter boxes onsite.
          </div>
        )}
        {items.map((att) => (
          <div key={att.id} data-testid={`attach-item-${att.id}`}
            className="relative border-2 border-foreground bg-background aspect-square overflow-hidden group">
            <button className="absolute inset-0 w-full h-full flex items-center justify-center"
              data-testid={`attach-open-${att.id}`}
              onClick={() => openAttachment(att)}>
              {att.mime.startsWith("image/") ? (
                <AttachmentThumb att={att} getData={getData} />
              ) : (
                <div className="flex flex-col items-center justify-center text-foreground p-2 text-center">
                  <FileText className="w-8 h-8" />
                  <span className="mt-1 font-mono text-[9px] uppercase truncate max-w-full">{att.name}</span>
                </div>
              )}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-foreground/60 flex items-center justify-center">
                <Eye className="w-5 h-5 text-background" />
              </div>
            </button>
            <button data-testid={`attach-delete-${att.id}`}
              onClick={(e) => { e.stopPropagation(); remove(att); }}
              className="absolute top-1 right-1 p-1 border-2 border-foreground bg-background hover:bg-destructive hover:text-destructive-foreground transition-colors">
              <Trash2 className="w-3 h-3" />
            </button>
            <div className="absolute bottom-0 inset-x-0 bg-foreground text-background px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest truncate">
              {humanSize(att.size)}
            </div>
          </div>
        ))}
      </div>

      {previewing && (
        <div className="fixed inset-0 bg-black/80 z-[2000] flex items-center justify-center p-4"
          onClick={() => setPreviewing(null)}
          data-testid="attach-preview">
          <div className="relative max-w-5xl max-h-full" onClick={(e) => e.stopPropagation()}>
            {previewing.mime.startsWith("image/") ? (
              <img src={previewing.data} alt={previewing.name}
                className="max-h-[85vh] max-w-full border-2 border-background" />
            ) : (
              <iframe title={previewing.name} src={previewing.data}
                className="w-[85vw] h-[85vh] border-2 border-background bg-background" />
            )}
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="font-mono text-xs uppercase tracking-widest text-background">{previewing.name}</div>
              <a href={previewing.data} download={previewing.name}
                className="btn-ghost-rugged text-[11px] py-1.5 bg-background">Download</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Thumbnail — reads the cached data URL via getData(); the cache is shared with
// the fullscreen preview so we only hit the network once per attachment.
const AttachmentThumb = ({ att, getData }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getData(att.id)
      .then((data) => { if (!cancelled) setSrc(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [att.id, getData]);
  return src
    ? <img src={src} alt={att.name} className="w-full h-full object-cover" />
    : <ImageIcon className="w-8 h-8 text-muted-foreground" />;
};

export default Attachments;
